import CFFormField from '@risk-smart/themed-cloudscape-components/form-field';
import type { RatingLevel } from '@risksmart-app/atomic-ui';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledAssessmentSelector from 'src/components/form/controlled-assessment-selector';
import ControlledDatePicker from 'src/components/form/controlled-date-picker';
import { ControlledFileUpload } from 'src/components/form/controlled-file-upload/ControlledFileUpload';
import ControlledInternalAuditReportSelector from 'src/components/form/controlled-internal-audit-report-selector';
import { ControlledMultiImpactGrid } from 'src/components/form/controlled-multi-impact-rating';
import ControlledOversightAssessmentSelector from 'src/components/form/controlled-oversight-assessment-selector';
import ControlledRiskMultiSelect from 'src/components/form/controlled-risk-multi-select';
import ControlledSelect from 'src/components/form/controlled-select';
import ControlledTextarea from 'src/components/form/controlled-textarea';
import CustomisableFieldWrapper from 'src/components/form/form/customisable-form/CustomisableFieldWrapper';
import { useRiskSmartForm } from 'src/components/form/form/customisable-form/RiskSmartFormContext';
import { FormField } from 'src/components/form/form/FormField';
import Loading from 'src/components/loading';
import { useAggregation } from 'src/hooks/useAggregation';
import { useScoringSettings } from 'src/ratings/useScoringSettings';
import { useFormConfig } from 'src/utils/table/hooks/form/useFormConfig';

import type { ConfigType } from '@/hooks/queries/risk-assessment-result-config/types';

import type { AssessmentTypeEnum } from '../types';
import { getParentTypeFromControlType } from './getParentTypeFromControlType';
import type { MultiImpactRiskAssessmentResultFormDataFields } from './riskAssessmentResultSchema';
import { RiskAssessmentResultTestIds } from './RiskAssessmentResultTestIds';
import { useControlTypeLabel } from './useControlTypeLabel';

type Props = {
  readOnly?: boolean;
  showSelector?: AssessmentTypeEnum;
  disableRiskSelector: boolean;
  assessmentMode?: AssessmentTypeEnum;
  configType: ConfigType;
  // Config version the viewed rating was assessed under, so its levels and
  // categories resolve against that version (read-only stale ratings).
  configId?: string | null;
  onControlTypeChange: (
    controlType: Risk_Assessment_Result_Control_Type_Enum
  ) => void;
};

const MultiImpactRiskAssessmentResultFormFields: FC<Props> = ({
  readOnly,
  onControlTypeChange,
  showSelector,
  disableRiskSelector,
  assessmentMode,
  configType,
  configId,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'assessmentResults.fields',
  });
  const [toolsContent, _] = useTools();

  const getControlTypeLabel = useControlTypeLabel();
  const { control, watch, setError, clearErrors, getValues, formState } =
    useFormContext<MultiImpactRiskAssessmentResultFormDataFields>();
  const { setBeforeSaveHooks } = useRiskSmartForm();
  const { loading } = useAggregation();
  const {
    loading: scoringLoading,
    getOptionsByCategory,
    getImpactCategories,
  } = useScoringSettings({
    configType,
    configIds: configId ? [configId] : undefined,
  });
  const impactCategories = useMemo(
    () => getImpactCategories(configId),
    [getImpactCategories, configId]
  );

  const controlType = watch('ControlType');
  useEffect(() => {
    onControlTypeChange(controlType);
  }, [controlType, onControlTypeChange]);

  const validateRatings = useCallback(async (): Promise<boolean> => {
    const values = getValues();
    let valid = true;

    clearErrors('Likelihood');
    clearErrors('ImpactRatings');

    if (values.Likelihood == null) {
      setError('Likelihood', {
        type: 'required',
        message: 'A likelihood rating is required',
      });
      valid = false;
    }

    const missingCategories = values.ImpactRatings.filter(
      (r) => r.rating == null
    ).map((r) => r.categoryName);

    if (missingCategories.length > 0) {
      setError('ImpactRatings', {
        type: 'required',
        message: 'All impact ratings are required',
      });
      valid = false;
    }

    return valid;
  }, [getValues, setError, clearErrors]);

  useEffect(() => {
    setBeforeSaveHooks((prev) => [...prev, validateRatings]);

    return () => {
      setBeforeSaveHooks((prev) =>
        prev.filter((hook) => hook !== validateRatings)
      );
    };
  }, [validateRatings, setBeforeSaveHooks]);

  const formId = getParentTypeFromControlType(controlType, assessmentMode);
  const formConfig = useFormConfig(formId);

  const likelihoodLevels = useMemo<RatingLevel[]>(
    () =>
      getOptionsByCategory('likelihood', configId).map((o) => ({
        value: o.value,
        label: o.label,
        color: o.color ?? '#cccccc',
      })),
    [getOptionsByCategory, configId]
  );

  const impactLevels = useMemo<RatingLevel[]>(
    () =>
      getOptionsByCategory('impact', configId).map((o) => ({
        value: o.value,
        label: o.label,
        color: o.color ?? '#cccccc',
      })),
    [getOptionsByCategory, configId]
  );

  if (loading || scoringLoading) {
    return <Loading />;
  }

  return (
    <CustomisableFieldWrapper readOnly={readOnly}>
      {showSelector === 'rating' && (
        <ControlledAssessmentSelector
          defaultRequired={toolsContent === 'wizard'}
          addEmptyOption={true}
          readOnly={readOnly}
          disabled={readOnly}
          key={'AssessmentId'}
          name={formConfig.AssessmentId.fieldId}
          label={formConfig.AssessmentId.formLabel}
          description={t('Assessment_help')}
          control={control}
          testId={RiskAssessmentResultTestIds.Assessment}
          expandToViewport
        />
      )}
      {showSelector === 'compliance_monitoring_assessment' && (
        <ControlledOversightAssessmentSelector
          defaultRequired={true}
          readOnly={readOnly}
          disabled={readOnly || disableRiskSelector}
          key={'OversightAssessmentId'}
          name={formConfig.OversightAssessmentId.fieldId}
          label={formConfig.OversightAssessmentId.formLabel}
          description={t('OversightAssessment_help')}
          control={control}
          testId={RiskAssessmentResultTestIds.OversightAssessment}
        />
      )}
      {showSelector === 'internal_audit_report' && (
        <ControlledInternalAuditReportSelector
          defaultRequired={false}
          readOnly={readOnly}
          disabled={readOnly || disableRiskSelector}
          key={'InternalAuditReportId'}
          name={formConfig.InternalAuditReportId.fieldId}
          label={formConfig.InternalAuditReportId.formLabel}
          description={t('InternalAuditReport_help')}
          control={control}
          testId={RiskAssessmentResultTestIds.InternalAuditReport}
        />
      )}
      <ControlledRiskMultiSelect
        defaultRequired={true}
        forceRequired={true}
        key={'RiskIds'}
        testId={RiskAssessmentResultTestIds.Risk}
        control={control}
        label={formConfig.RiskIds.formLabel}
        description={t('Risk_help')}
        disabled={readOnly || disableRiskSelector}
        name={formConfig.RiskIds.fieldId}
        placeholder={t('Risk_placeholder')}
        expandToViewport
      />
      <ControlledSelect
        defaultRequired={true}
        forceRequired={true}
        key={'controlType'}
        options={[
          {
            value: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
            label: getControlTypeLabel(
              Risk_Assessment_Result_Control_Type_Enum.Uncontrolled
            ),
          },
          {
            value: Risk_Assessment_Result_Control_Type_Enum.Controlled,
            label: getControlTypeLabel(
              Risk_Assessment_Result_Control_Type_Enum.Controlled
            ),
          },
        ]}
        name={formConfig.ControlType.fieldId}
        label={formConfig.ControlType.formLabel}
        description={
          disableRiskSelector
            ? t('ControlType_help_readonly')
            : t('ControlType_help')
        }
        placeholder={t('ControlType_placeholder')}
        control={control}
        testId={RiskAssessmentResultTestIds.ControlType}
        disabled={readOnly || disableRiskSelector}
        expandToViewport
      />
      <FormField key={'MultiImpactRating'} label={'Ratings'} stretch>
        <div className={'mb-3'}>
          <ControlledMultiImpactGrid
            likelihoodLevels={likelihoodLevels}
            likelihoodLabel={formConfig.Likelihood.formLabel}
            impactLevels={impactLevels}
            impactCategories={impactCategories}
            readOnly={readOnly}
            onLikelihoodChange={() => clearErrors('Likelihood')}
            onImpactRatingChange={() => clearErrors('ImpactRatings')}
          />
        </div>
        {formState.errors.Likelihood && (
          <CFFormField errorText={formState.errors.Likelihood.message}>
            <></>
          </CFFormField>
        )}
        {formState.errors.ImpactRatings && (
          <CFFormField errorText={formState.errors.ImpactRatings.message}>
            <></>
          </CFFormField>
        )}
      </FormField>
      <ControlledTextarea
        defaultRequired={false}
        key={'rationale'}
        testId={'rationale'}
        name={formConfig.Rationale.fieldId}
        label={formConfig.Rationale.formLabel}
        description={t('Rationale_help')}
        placeholder={t('Rationale_placeholder')}
        control={control}
        disabled={readOnly}
      />
      <ControlledDatePicker
        defaultRequired={true}
        name={formConfig.TestDate.fieldId}
        key={'testDate'}
        label={formConfig.TestDate.formLabel}
        testId={RiskAssessmentResultTestIds.TestDate}
        description={t('TestDate_help')}
        control={control}
        disabled={readOnly}
      />
      <ControlledFileUpload
        key={'files'}
        testId={'attachFiles'}
        label={formConfig.files.formLabel}
        description={t('newFiles_help')}
        control={control}
        name={formConfig.files.fieldId}
        disabled={readOnly}
      />
    </CustomisableFieldWrapper>
  );
};

export default MultiImpactRiskAssessmentResultFormFields;
